/**
 * SyncUserByEmailService
 * 
 * A centralized service for synchronizing user data with the cloud function.
 * This service handles all communication with the syncUserByEmail cloud function.
 */

import { PLAN_TYPE } from '../constants/subscriptionTypes';
import * as localStorageService from './local-storage-service';

// Get the functions URL from environment variables
const getFunctionsUrl = () => {
  return process.env.REACT_APP_FUNCTIONS_BASE_URL || 'https://us-central1-thinkgpt.cloudfunctions.net';
};

/**
 * Prepare user data object from Firebase user
 * @param {Object} currentUser - Firebase user object
 * @returns {Object} Formatted user data object
 */
export const prepareUserData = (currentUser) => {
  return {
    email: currentUser.email,
    family_name: currentUser.displayName?.split(' ').slice(-1)[0] || '',
    given_name: currentUser.displayName?.split(' ')[0] || '',
    id: currentUser.uid,
    firebaseAuthUid: currentUser.uid,
    name: currentUser.displayName,
    picture: currentUser.photoURL,
    verified_email: currentUser.emailVerified,
    authSource: 'webapp',
    stripeSubscriptionType: PLAN_TYPE.FREE // Default value, will be overridden if exists
  };
};

/**
 * Sync user data with the cloud function
 * @param {Object} currentUser - Firebase user object
 * @param {boolean} debug - Whether to log debug information
 * @returns {Promise<Object>} Result object with status and data
 */
export const syncUserByEmail = async (currentUser, debug = false) => {
  if (!currentUser || !currentUser.email) {
    throw new Error('User or email is missing');
  }

  try {
    const userData = prepareUserData(currentUser);
    const functionsUrl = getFunctionsUrl();
    
    if (debug) {
      console.log('syncUserByEmail: Calling cloud function for user:', currentUser.email);
    }
    
    const response = await fetch(`${functionsUrl}/syncUserByEmail`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        email: currentUser.email,
        uid: currentUser.uid,
        userData: userData,
        source: 'web'
      })
    });
    
    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }
    
    const result = await response.json();
    
    if (debug) {
      console.log('syncUserByEmail: Response received:', result);
    }
    
    // Always store the email in local storage for reference by cloud functions
    localStorageService.storeUserEmail(currentUser.email);
    
    if (result.status === 'success') {
      if (debug) {
        console.log('syncUserByEmail: User document successfully updated');
      }
      
      // Store the complete user data in localStorage for reuse
      if (result.data) {
        // The API response structure has the actual user data in result.data.currentUser
        const userData = result.data.currentUser || result.data;
        
        if (debug) {
          console.log('syncUserByEmail: About to store user data with subscription type:', userData.stripeSubscriptionType);
        }
        
        // Use local storage service to store user data
        localStorageService.storeUserData(userData, debug);
        
        if (debug) {
          console.log('syncUserByEmail: User data stored in localStorage');
        }
      }
      
      return {
        status: 'success',
        data: result.data
      };
    } else {
      if (debug) {
        console.error('syncUserByEmail: Error from cloud function:', result.message);
      }
      
      return {
        status: 'error',
        message: result.message || 'Unknown error from syncUserByEmail'
      };
    }
  } catch (error) {
    console.error('syncUserByEmail: Error:', error);
    return {
      status: 'error',
      message: error.message || 'Unknown error in syncUserByEmail'
    };
  }
};

// Export all functions
const syncUserByEmailService = {
  prepareUserData,
  syncUserByEmail,
  getFunctionsUrl
};

export default syncUserByEmailService;
